<template>
    <div class="inline-block">
        <div v-if="formOpen === false">
            <template v-if="isEdit">
                <template v-if="useSquareButton">
                    <div class="flex space-x-2">
                        <slot name="inline-text" />
                        <button
                            :title="buttonText"
                            type="button"
                            @click="open()"
                            class="text-indigo-700 bg-indigo-100 hover:bg-indigo-200 inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <svg viewBox="0 0 20 20" fill="currentColor" class="pencil w-4 h-4">
                                <path
                                    d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                                ></path>
                            </svg>
                        </button>
                    </div>
                </template>
                <button
                    v-else
                    type="button"
                    @click="open()"
                    class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <svg viewBox="0 0 20 20" fill="currentColor" class="pencil w-4 h-4">
                        <path
                            d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                        ></path>
                    </svg>
                </button>
            </template>
            <template v-else>
                <AppButton element="button" type="button" @click="open()" class="flex items-center space-x-2">
                    <svg
                        class="w-6 h-6"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                        v-if="withCreateIcon"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                    {{ buttonText }}
                </AppButton>
            </template>
        </div>
        <template v-else>
            <component :is="isInline ? 'span' : 'Modal'" :show="formOpen" @close="formOpen = false">
                <form :action="endpoint.url" method="post" @submit.prevent="submit()">
                    <div class="flex space-x-2 items-center" :class="isEdit ? 'justify-between' : ''">
                        <div class="mt-1 w-full">
                            <FormInput
                                required
                                id="name"
                                v-model="form.text"
                                :name="column"
                                ref="text"
                                type="text"
                                maxlength="180"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors[column]" class="mt-2" />
                        </div>

                        <div class="shrink-0">
                            <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                                {{ $t('messages.save') }}
                                <span v-if="form.processing" class="ml-1 btn-spinner" />
                            </AppButton>

                            <AppButton element="button" type="button" @click="formOpen = !formOpen">
                                {{ $t('messages.cancel') }}
                            </AppButton>
                        </div>
                    </div>
                </form>
            </component>
        </template>
    </div>
</template>

<script>
import FormError from '@/components/Forms/FormError.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import AppButton from '@/components/AppButton.vue';
import Modal from '@/components/Modal.vue';

export default {
    name: 'SingleTextModalForm',
    components: {
        FormLabel,
        FormInput,
        FormError,
        AppButton,
        Modal,
    },
    props: {
        endpoint: {
            type: Object,
            required: true,
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
        text: {
            type: String,
            required: false,
            default: '',
        },
        column: {
            type: String,
            required: false,
            default: 'name',
        },
        buttonText: {
            type: String,
            required: true,
        },
        withCreateIcon: {
            type: Boolean,
            default: false,
            required: false,
        },
        isInline: {
            type: Boolean,
            default: false,
            required: false,
        },
        useSquareButton: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            formOpen: false,
            form: this.$inertia.form({
                text: this.text,
            }),
        };
    },
    methods: {
        submit() {
            this.form
                .transform((data) => {
                    const newData = {};
                    newData[this.column] = data.text;
                    return newData;
                })
                .submit(this.endpoint.method, this.endpoint.url, {
                    onSuccess: () => {
                        if (!this.text) {
                            this.form.reset('text');
                        }
                        this.formOpen = false;
                    },
                    preserveScroll: true,
                });
        },
        cancel() {
            this.formOpen = false;
            this.form.reset('text');
            this.form.clearErrors();
        },
        open() {
            this.formOpen = true;
            this.focusInput();
        },
        focusInput() {
            this.$nextTick(function () {
                this.$refs.text.focusInput();
            });
        },
        onEscape(e) {
            if (e.key === 'Esc' || e.key === 'Escape') {
                this.open = false;
            }
        },
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.onEscape);
    },
    mounted() {
        document.addEventListener('keydown', this.onEscape);
    },
};
</script>
